import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { errorMessage } from "../lib/api";
import { PageHeader, ActionButton, GhostButton, PillBadge } from "../components/Primitives";
import { Modal } from "../components/Modal";
import {
  AlertTriangle, CheckCircle2, Clock, XCircle, MapPin, MessageSquare, ArrowRight, BellRing,
} from "lucide-react";

const STATUS_META = {
  open:       { label: "Open",       Icon: AlertTriangle, color: "#EAB308" },
  in_review:  { label: "In review",  Icon: Clock,         color: "#0A84FF" },
  resolved:   { label: "Resolved",   Icon: CheckCircle2,  color: "#22C55E" },
  dismissed:  { label: "Dismissed",  Icon: XCircle,       color: "#6B7280" },
};

const SEVERITY_TONE = { low: "default", normal: "blue", high: "amber", urgent: "red" };

const STATUS_ORDER = ["open", "in_review", "resolved", "dismissed"];

/**
 * Admin page — inbox of every complaint filed across the tenant's clients.
 * Route: /portal-complaints
 * Admin scope only.
 */
export default function AdminComplaints() {
  const [rows, setRows]   = useState([]);
  const [filter, setFilter] = useState("open");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [notes, setNotes]     = useState("");
  const [newStatus, setNewStatus] = useState("in_review");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const r = await api.get("/portal/complaints", { params });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error(errorMessage(err, "Failed to load complaints"));
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const openEdit = (c) => {
    setEditing(c);
    setNotes(c.resolution_notes || "");
    setNewStatus(c.status === "open" ? "in_review" : c.status);
  };

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await api.patch(`/portal/complaints/${editing.id}`, {
        status: newStatus,
        resolution_notes: notes,
      });
      toast.success("Complaint updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Update failed"));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-complaints-page">
      <PageHeader
        title="Client Complaints"
        subtitle="Complaints filed by clients through the portal — reply, transition status, and track resolution."
        action={
          <div className="inline-flex items-center gap-1 border border-[#27272A] p-0.5">
            {["open", "in_review", "resolved", "all"].map((k) => (
              <button
                key={k}
                data-testid={`filter-${k}`}
                onClick={() => setFilter(k)}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-widest transition-colors ${
                  filter === k
                    ? "bg-[#FF3B30] text-white"
                    : "text-zinc-400 hover:text-zinc-200"
                }`}
              >
                {k.replace("_", " ")}
              </button>
            ))}
          </div>
        }
      />

      <div className="p-8">
        {loading ? (
          <div className="border border-[#27272A] bg-[#0A0A0A] p-16 text-center text-zinc-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="border border-[#27272A] bg-[#0A0A0A] p-12 text-center">
            <div className="label-mute mb-2">No complaints in this view</div>
            <div className="text-zinc-400 text-sm">
              Nothing matches the <span className="font-mono">{filter}</span> filter right now.
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-complaints-list">
            {rows.map((c) => {
              const meta = STATUS_META[c.status] || STATUS_META.open;
              const Icon = meta.Icon;
              return (
                <div
                  key={c.id}
                  className="border border-[#27272A] bg-[#121212] p-5 hover:border-[#3F3F46] transition-colors cursor-pointer"
                  data-testid={`admin-complaint-${c.id}`}
                  onClick={() => openEdit(c)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold border"
                          style={{ color: meta.color, borderColor: `${meta.color}55`, backgroundColor: `${meta.color}12` }}
                        >
                          <Icon className="size-3" /> {meta.label}
                        </span>
                        <PillBadge tone={SEVERITY_TONE[c.severity] || "default"}>{c.severity}</PillBadge>
                        {c.escalated_at && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold text-[#EF4444] border border-[#EF4444]/40 bg-[#EF4444]/10"
                            title={`Escalated to ${(c.escalated_to || []).join(", ")} at ${new Date(c.escalated_at).toLocaleString()}`}
                          >
                            <BellRing className="size-2.5" /> ESCALATED
                          </span>
                        )}
                        <span className="text-xs text-zinc-500 font-mono">
                          {new Date(c.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        </span>
                        {c.client_name && (
                          <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">
                            {c.client_name}
                          </span>
                        )}
                        {c.site_name && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500">
                            <MapPin className="size-3" /> {c.site_name}
                          </span>
                        )}
                        <span className="text-[10px] text-zinc-500">by {c.user_name}</span>
                      </div>
                      <div className="font-display font-bold text-base tracking-tight mt-2">{c.subject}</div>
                      <div className="text-sm text-zinc-400 mt-1 line-clamp-2 whitespace-pre-wrap">{c.body}</div>
                      {c.resolution_notes && (
                        <div className="mt-2 border-l-2 border-[#22C55E] pl-3 text-xs text-zinc-400">
                          <MessageSquare className="size-3 inline mr-1" />
                          {c.resolution_notes.slice(0, 200)}
                          {c.resolution_notes.length > 200 && "…"}
                        </div>
                      )}
                    </div>
                    <ArrowRight className="size-4 text-zinc-600 flex-shrink-0 mt-1" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.subject} testId="admin-complaint-modal" wide>
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <PillBadge tone={SEVERITY_TONE[editing.severity] || "default"}>{editing.severity}</PillBadge>
              {editing.client_name && (
                <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">
                  {editing.client_name}
                </span>
              )}
              {editing.site_name && (
                <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-zinc-500">
                  <MapPin className="size-3" /> {editing.site_name}
                </span>
              )}
              <span className="text-xs text-zinc-500 font-mono">
                Filed {new Date(editing.created_at).toLocaleString()} · by {editing.user_name}
              </span>
            </div>
            <div className="whitespace-pre-wrap text-sm text-zinc-200 bg-[#0A0A0A] p-4 border border-[#27272A]">
              {editing.body}
            </div>
            <div>
              <div className="label-mute mb-1">Status</div>
              <div className="inline-flex items-center gap-1 border border-[#27272A] p-0.5">
                {STATUS_ORDER.map((k) => (
                  <button
                    key={k}
                    onClick={() => setNewStatus(k)}
                    data-testid={`status-${k}`}
                    className={`px-3 py-1.5 text-[11px] uppercase tracking-widest transition-colors ${
                      newStatus === k
                        ? "bg-[#FF3B30] text-white"
                        : "text-zinc-400 hover:text-zinc-200"
                    }`}
                  >
                    {k.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="label-mute mb-1">Response to client (shown on their portal)</div>
              <textarea
                rows={5}
                className="w-full bg-[#0A0A0A] border border-[#27272A] p-3 text-sm text-zinc-200 outline-none focus:border-[#FF3B30]"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Explain what you did, what will happen next, or apologise sincerely if warranted."
                data-testid="admin-complaint-notes"
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#27272A]">
              <GhostButton onClick={() => setEditing(null)}>Close</GhostButton>
              <ActionButton
                data-testid="admin-complaint-save"
                onClick={save}
                disabled={busy}
                className={busy ? "opacity-50 cursor-not-allowed" : ""}
              >
                {busy ? "Saving…" : "Save changes"}
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
